/**
 * frontend/src/components/MissionCard.jsx
 * SoundDAO — AI-generated dynamic missions display (Sprint 3 M2)
 *
 * Props:
 *   userId  — user UUID
 *   persona — 'player' | 'creator' | 'collector'
 *
 * Behaviour:
 *   - On mount: fetches GET /api/missions/:userId?persona=X
 *   - Shows loading skeleton while fetching or generating
 *   - Shows 3 mission rows with title, description, reward, days left
 *   - Completed missions show green ✓ and are greyed out
 *   - Refresh button calls POST /api/missions/generate to force fresh missions
 *   - If award response includes mission_completed: true, shows celebration message
 */

import React, { useState, useEffect, useCallback } from 'react';
import './MissionCard.css';
import { useRates } from '../lib/rates';
import { authFetch } from '../lib/authFetch';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';

// Icon per persona
const PERSONA_ICON = {
  player:    '🎯',
  creator:   '🎤',
  collector: '💎',
};

// Action labels for display
// FReN is not launched (no collection, no purchase, no chain decided): any fren_* mission
// renders "Coming soon" and never a button.
const ACTION_LABEL = {
  recruit_creator: 'Recruit a creator',
  playlist_share:  'Share a playlist',
  playlist_build:  'Build a playlist',
  refer_player:    'Refer a fan',
  creator_content: 'Post content',
  watchlist_add:   'Add to watchlist',
};
const isFren = (action) => typeof action === 'string' && action.startsWith('fren_');

// Open missions show tonight's live amount for the action; completed ones show what was stored.
function missionReward(mission, rates) {
  const live = rates?.rates?.[mission.action];
  return !mission.completed && Number.isFinite(live) ? live : mission.reward_sound;
}

function daysUntil(isoString) {
  const ms   = new Date(isoString) - Date.now();
  const days = Math.ceil(ms / (1000 * 60 * 60 * 24));
  return Math.max(0, days);
}

// ── Skeleton row ───────────────────────────────────────────────────────────────
function MissionSkeleton() {
  return (
    <div className="mission-row mission-row--skeleton">
      <div className="mission-row-icon skeleton-block skeleton-block--icon" />
      <div className="mission-row-body">
        <div className="skeleton-block skeleton-block--title" />
        <div className="skeleton-block skeleton-block--desc" />
      </div>
      <div className="mission-row-right">
        <div className="skeleton-block skeleton-block--reward" />
      </div>
    </div>
  );
}

// ── Single mission row ─────────────────────────────────────────────────────────
function MissionRow({ mission, celebrating, onAction, rates }) {
  const days = daysUntil(mission.expires_at);
  const done = mission.completed;

  return (
    <div className={`mission-row${done ? ' mission-row--done' : ''}${celebrating ? ' mission-row--celebrate' : ''}`}>
      <div className="mission-row-icon">
        {done ? '✅' : ACTION_LABEL[mission.action] ? '🎯' : '⭐'}
      </div>

      <div className="mission-row-body">
        <p className="mission-row-title">{mission.title}</p>
        <p className="mission-row-desc">{mission.description}</p>
        {!done && isFren(mission.action) ? (
          <span className="fren-coming-soon">🔜 Coming Soon</span>
        ) : !done ? (
          <button
            className="mission-row-action"
            onClick={() => onAction && onAction(mission.action)}
          >
            {ACTION_LABEL[mission.action] || mission.action}
          </button>
        ) : null}
        {celebrating && (
          <p className="mission-row-celebrate-msg">🎉 Mission complete!</p>
        )}
      </div>

      <div className="mission-row-right">
        <span className={`mission-row-reward${done ? ' mission-row-reward--done' : ''}`}>
          {Number.isFinite(missionReward(mission, rates)) ? `+${missionReward(mission, rates).toLocaleString()} ` : ''}SOUND
        </span>
        {!done && days <= 3 && (
          <span className="mission-row-expiry mission-row-expiry--urgent">{days}d left</span>
        )}
        {!done && days > 3 && (
          <span className="mission-row-expiry">{days}d left</span>
        )}
        {done && (
          <span className="mission-row-expiry mission-row-expiry--done">Earned</span>
        )}
      </div>
    </div>
  );
}

// ── MissionCard ────────────────────────────────────────────────────────────────
export default function MissionCard({ userId, persona, onAction }) {
  const rates = useRates();
  const [missions,     setMissions]     = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [refreshing,   setRefreshing]   = useState(false);
  const [error,        setError]        = useState(null);
  const [celebrating,  setCelebrating]  = useState({}); // { [missionId]: true }

  const icon = PERSONA_ICON[persona] || '🎯';

  // ── Fetch missions ─────────────────────────────────────────────────────────
  const fetchMissions = useCallback(async () => {
    if (!userId || !persona) return;
    setError(null);
    try {
      const res = await authFetch(`${API_BASE}/api/missions/${userId}?persona=${persona}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load missions');
      setMissions(data.missions || []);
    } catch (err) {
      console.error('[MissionCard] fetchMissions error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [userId, persona]);

  useEffect(() => {
    fetchMissions();
  }, [fetchMissions]);

  // ── Refresh (force new generation) ────────────────────────────────────────
  const handleRefresh = async () => {
    setRefreshing(true);
    setError(null);
    try {
      // Force new missions by clearing from generate endpoint with override flag
      // We delete existing active missions first, then generate
      const genRes = await authFetch(`${API_BASE}/api/missions/generate`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ user_id: userId, persona, force: true }),
      });
      const genData = await genRes.json();
      if (!genRes.ok) throw new Error(genData.error || 'Generation failed');
      setMissions(genData.missions || []);
    } catch (err) {
      console.error('[MissionCard] handleRefresh error:', err);
      setError(err.message);
    } finally {
      setRefreshing(false);
    }
  };

  // ── Expose completeMission for parent components to call after award ───────
  MissionCard.notifyMissionCompleted = (actionType) => {
    setMissions(prev =>
      prev.map(m => {
        if (!m.completed && m.action === actionType) {
          setCelebrating(c => ({ ...c, [m.id]: true }));
          setTimeout(() => setCelebrating(c => { const n = { ...c }; delete n[m.id]; return n; }), 4000);
          return { ...m, completed: true };
        }
        return m;
      })
    );
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  const completedCount = missions.filter(m => m.completed).length;

  return (
    <div className="mission-card">
      <div className="mission-card-header">
        <span className="mission-card-icon">{icon}</span>
        <h3 className="mission-card-title">Your Missions</h3>
        {!loading && missions.length > 0 && (
          <span className="mission-card-progress">
            {completedCount}/{missions.length}
          </span>
        )}
        <button
          className={`mission-card-refresh${refreshing ? ' mission-card-refresh--spinning' : ''}`}
          onClick={handleRefresh}
          disabled={loading || refreshing}
          title="Generate new missions"
        >
          ↻
        </button>
      </div>

      <div className="mission-card-body">
        {loading ? (
          <>
            <MissionSkeleton />
            <MissionSkeleton />
            <MissionSkeleton />
          </>
        ) : error ? (
          <div className="mission-card-error">
            <p className="mission-error-msg">⚠️ {error}</p>
            <button className="mission-error-retry" onClick={fetchMissions}>
              Try again
            </button>
          </div>
        ) : missions.length === 0 ? (
          <div className="mission-card-empty">
            <p>No missions yet.</p>
            <button className="mission-error-retry" onClick={handleRefresh}>
              Generate missions
            </button>
          </div>
        ) : (
          missions.map(mission => (
            <MissionRow
              rates={rates}
              key={mission.id}
              mission={mission}
              celebrating={!!celebrating[mission.id]}
              onAction={onAction}
            />
          ))
        )}
      </div>
    </div>
  );
}
